import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {getViewForViewReference} from 'analysis-tool/data-view/AnalysisViewUtils';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertPipelineStepIsStepType, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getDefaultClientIdForView, getParentViewsForView} from 'pipelines/PipelineViewUtils';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Overrides for how to render views in the sidebar.
 */
export interface ViewSidebarOverrides {
  /**
   * Set of view IDs to skip rendering as normal children.
   */
  skipViewIds: Set<string>;
  /**
   * Additional children for parent views outside of the normal view topology, keyed by parent view ID.
   */
  topologyOverrides: Map<string, Array<TopologyOverride>>;
  /**
   * When true, layout views are excluded from child sidebar items (because they are rendered
   * in their own dedicated "Layouts" section).
   */
  excludeLayoutChildViews: boolean;
}

export interface TopologyOverride {
  childViews: Array<PipelineViewGraphQL>;
  // If specified, creates an artificial grouping item in the sidebar, which links to the first child view
  groupingItem?: {
    title: string;
    icon: BenchlingIconName;
  };
}

type TopologyOverrideResult = Pick<ViewSidebarOverrides, 'skipViewIds' | 'topologyOverrides'>;

/**
 * TODO(BNCH-125769): Remove this logic
 *
 * Get regression-specific topology overrides.
 *
 * Regressions generate 3 views, but by default we only want to show the chart view in the
 * sidebar. We'll show the non-chart views if they have any children.
 *
 * For regression views whose parent is a DATASET_VIEW view, skip that parent view and
 * nest the regression views directly under the ancestor.
 */
export function getRegressionTopologyOverrides(analysis: AnalysisGraphQL): TopologyOverrideResult {
  const skipViewIds = new Set<string>();
  const topologyOverrides = new Map<string, Array<TopologyOverride>>();

  const regressionViewsByStepId = new Map<string, Array<PipelineViewGraphQL>>();
  analysis.views
    .filter(
      (view) => view.lineage.length > 0 && view.lineage[0].step.stepType === PipelineStepType.REGRESSION
    )
    .forEach((regressionView) => {
      const stepId = regressionView.lineage[0].step.id;
      if (!regressionViewsByStepId.has(stepId)) {
        regressionViewsByStepId.set(stepId, []);
      }
      regressionViewsByStepId.get(stepId)!.push(regressionView);
    });

  regressionViewsByStepId.forEach((regressionViews) => {
    const viewsToShow: Array<PipelineViewGraphQL> = [];
    regressionViews.forEach((view) => {
      const stepOutputReference = view.lineage[0].stepOutputReference;
      if (stepOutputReference == null) {
        skipViewIds.add(getDefaultClientIdForView(view));
        return;
      }
      const outputStepDataName = stepOutputReference.outputStepData.name;
      if (
        outputStepDataName === 'Output chart' ||
        view.lineage[0].step.outgoingEdges.filter((edge) => edge.fromStepData.name === outputStepDataName)
          .length > 0
      ) {
        viewsToShow.push(view);
      } else {
        skipViewIds.add(getDefaultClientIdForView(view));
      }
    });

    if (viewsToShow.length === 0) {
      return;
    }

    // Parent view might not exist if the regression step was orphaned by deleting its incoming edge via
    // the flowchart.
    const [parentView] = getParentViewsForView(analysis, viewsToShow[0]);
    if (parentView?.lineage[0]?.step.stepType === PipelineStepType.DATASET_VIEW) {
      const [ancestorView] = getParentViewsForView(analysis, parentView);
      // Ancestor view might not exist if the dataset view step was orphaned by deleting its incoming edge via
      // the flowchart.
      if (ancestorView) {
        const parentViewId = getDefaultClientIdForView(ancestorView);
        const existing = topologyOverrides.get(parentViewId) ?? [];
        topologyOverrides.set(parentViewId, [...existing, {childViews: viewsToShow}]);
      }
    }
  });

  return {skipViewIds, topologyOverrides};
}

/**
 * Get topology overrides for layout views to customize their sidebar placement.
 *
 * If a layout has a sidebarParent config specified, then ensure that the layout is nested directly under the
 * parent. If the layout also has a sidebarGrouping config, then ensure that all layouts with corresponding
 * groupings get grouped together.
 */
export function getLayoutTopologyOverrides(analysis: AnalysisGraphQL): TopologyOverrideResult {
  const groupingKeyToEntry: Map<string | null, {parentViewId: string; override: TopologyOverride}> =
    new Map();

  analysis.views.forEach((view) => {
    const lastStep = view.lineage[view.lineage.length - 1].step;

    if (lastStep.stepType !== PipelineStepType.LAYOUT) {
      return;
    }

    const sidebarParent = getLayoutSidebarParentViewId(analysis, view);
    if (sidebarParent == null) {
      return;
    }

    const layoutConfig = lastStep.config;
    assert(layoutConfig != null, 'Expected non-null layout config');
    const sidebarGrouping = layoutConfig.sidebarGrouping;
    const key = sidebarGrouping?.key ?? null;
    if (!groupingKeyToEntry.has(key)) {
      const override: TopologyOverride = {
        childViews: [],
        ...(sidebarGrouping?.displayName
          ? {groupingItem: {title: sidebarGrouping.displayName, icon: 'wave-sine' as BenchlingIconName}}
          : {}),
      };
      groupingKeyToEntry.set(key, {parentViewId: sidebarParent, override});
    }

    groupingKeyToEntry.get(key)!.override.childViews.push(view);
  });

  const topologyOverrides = new Map<string, Array<TopologyOverride>>();
  groupingKeyToEntry.forEach(({parentViewId, override}) => {
    const existing = topologyOverrides.get(parentViewId) ?? [];
    topologyOverrides.set(parentViewId, [...existing, override]);
  });

  return {skipViewIds: new Set(), topologyOverrides};
}

function getLayoutSidebarParentViewId(
  analysis: AnalysisGraphQL,
  layoutView: PipelineViewGraphQL
): string | null {
  const layoutStep = layoutView.lineage[layoutView.lineage.length - 1].step;
  assertPipelineStepIsStepType(PipelineStepType.LAYOUT, 0, layoutStep);
  const sidebarParentReference = layoutStep.config?.sidebarParent;
  const sidebarParentEdge = layoutStep.incomingEdges.find(
    (incomingEdge) =>
      incomingEdge.toStepData.name === sidebarParentReference?.inputDataName &&
      incomingEdge.toStepDataIndex === sidebarParentReference?.inputDataIndex
  );
  if (sidebarParentEdge == null) {
    return null;
  }

  const sidebarParentView = getViewForViewReference(analysis, {
    stepId: sidebarParentEdge.fromStep.id,
    outputReference: {
      stepDataName: sidebarParentEdge.fromStepData.name,
      rawDataIndex: sidebarParentEdge.fromStepDataIndex,
    },
  });
  if (sidebarParentView == null) {
    return null;
  }
  return getDefaultClientIdForView(sidebarParentView);
}

/**
 * Generate the viewSidebarOverrides object used to modify the sidebar structure.
 *
 * The overrides we apply are:
 *  1. For regression steps, prefer the chart view in the sidebar: skip non-chart regression views
 *     unless they have downstream children (outgoing edges). Non-chart views with children are
 *     shown alongside the chart view.
 *  2. For regression views whose parent is a DATASET_VIEW view, skip that parent view.
 *  3. Get layout topology overrides following the logic in getLayoutTopologyOverrides.
 *  4. Automatically skip all views in the ancestor chain between a parent and child in a topology
 *     override.
 */
export function getViewSidebarOverrides(
  analysis: AnalysisGraphQL,
  {excludeLayoutChildViews = false}: {excludeLayoutChildViews?: boolean} = {}
): ViewSidebarOverrides {
  const overrideResults = [getRegressionTopologyOverrides(analysis), getLayoutTopologyOverrides(analysis)];
  const skipViewIds = new Set(overrideResults.flatMap((result) => [...result.skipViewIds]));
  const topologyOverrides = new Map<string, Array<TopologyOverride>>();
  overrideResults.forEach((result) => {
    result.topologyOverrides.forEach((entries, parentViewId) => {
      const existing = topologyOverrides.get(parentViewId) ?? [];
      topologyOverrides.set(parentViewId, [...existing, ...entries]);
    });
  });

  topologyOverrides.forEach((entries, parentViewId) => {
    entries
      .flatMap((entry) => entry.childViews)
      .forEach((childView) => {
        skipViewIds.add(getDefaultClientIdForView(childView));
        // Run a DFS to hide all views between the parent and child views
        const ancestorViews = getParentViewsForView(analysis, childView);
        while (ancestorViews.length > 0) {
          const ancestorView = ancestorViews.pop()!;
          const ancestorViewId = getDefaultClientIdForView(ancestorView);
          if (ancestorViewId === parentViewId || skipViewIds.has(ancestorViewId)) {
            continue;
          }

          skipViewIds.add(ancestorViewId);
          ancestorViews.push(...getParentViewsForView(analysis, ancestorView));
        }
      });
  });

  return {
    skipViewIds,
    topologyOverrides,
    excludeLayoutChildViews,
  };
}
